import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, ChevronUp, CheckCircle, Clock, FileText, AlertCircle } from 'lucide-react';

export default function ActivelyReviewedGuide() {
  const [openFAQ, setOpenFAQ] = useState(null);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      question: "Does 'actively reviewed' mean my case is approved?",
      answer: "No, not yet. It means USCIS is working on your case, but it doesn't guarantee approval. You might still get a Request for Evidence (RFE), an interview notice, or your case could stay pending for more review."
    },
    {
      question: "Can it stay like this for months?",
      answer: "Yes, unfortunately it can. Some cases update to 'actively reviewed' and then don't change for weeks or even months. It depends on your case type, which USCIS office is handling it, and current backlogs. Check normal processing times for your form to see if you're still within range."
    },
    {
      question: "Should I contact USCIS now?",
      answer: "Not yet. If your case is still within normal processing times, USCIS won't provide additional information. Contact them only if your case is significantly delayed beyond posted timelines, or if you haven't seen any update in several months."
    },
    {
      question: "Why did my status update twice to the same thing?",
      answer: "USCIS systems sometimes show duplicate updates when different officers review the same case, or when background checks complete. It's normal and doesn't mean anything went wrong."
    }
  ];

  const nextSteps = [
    {
      icon: <CheckCircle className="w-5 h-5" />,
      title: "Case Approved",
      description: "Your case is approved and you'll receive your approval notice"
    },
    {
      icon: <FileText className="w-5 h-5" />,
      title: "Request for Evidence (RFE)",
      description: "USCIS needs additional documents or clarification"
    },
    {
      icon: <Clock className="w-5 h-5" />,
      title: "Interview Scheduled",
      description: "You'll receive a notice for an in-person interview"
    },
    {
      icon: <AlertCircle className="w-5 h-5" />,
      title: "Case Remains Pending",
      description: "No visible change for days, weeks, or months while review continues"
    }
  ];

  const actionItems = [
    "Monitor your USCIS online account regularly",
    "Check your mail daily (USCIS still sends physical notices)",
    "Keep all supporting documents organized and ready",
    "Stay patient - immigration processing takes time",
    "Compare your wait time to normal processing times for your form type"
  ];

  // Schema.org FAQ markup
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>What Does "Case Is Being Actively Reviewed" Mean? USCIS Explained</title>
        <meta 
          name="description" 
          content="Learn what 'Case Is Being Actively Reviewed' means, what may happen next, how long it can take, and what you should do now." 
        />
        <meta name="keywords" content="actively reviewed uscis meaning, case is being actively reviewed, uscis case status, immigration case review" />
        <link rel="canonical" href="https://uscistrack.com/actively-reviewed-uscis-meaning" />
        
        {/* Open Graph */}
        <meta property="og:title" content="What Does 'Case Is Being Actively Reviewed' Mean? USCIS Explained" />
        <meta property="og:description" content="Learn what 'Case Is Being Actively Reviewed' means, what may happen next, how long it can take, and what you should do now." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://uscistrack.com/actively-reviewed-uscis-meaning" />
        
        {/* JSON-LD Schema */}
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        {/* Header */}
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Link to="/" className="text-indigo-600 hover:text-indigo-700 font-semibold">
              ← Back to Home
            </Link>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-4xl mx-auto px-4 py-12">
          <article className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
            {/* H1 */}
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What Does "Case Is Being Actively Reviewed" Mean?
            </h1>

            {/* Intro */}
            <div className="prose prose-lg max-w-none mb-12">
              <p className="text-xl text-gray-700 leading-relaxed">
                If you just saw this status update and your heart started racing—you're not alone. 
                Many people worry when they see "Case Is Being Actively Reviewed by USCIS" appear in their case tracker.
              </p>
              <p className="text-lg text-gray-600">
                Let's break down what this actually means, in plain English.
              </p>
            </div>

            {/* Section 1 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                What This USCIS Status Usually Means
              </h2>
              <div className="bg-blue-50 border-l-4 border-indigo-500 p-6 mb-6">
                <p className="text-lg text-gray-800 font-medium mb-2">
                  In simple terms: Someone at USCIS is looking at your case.
                </p>
              </div>
              
              <p className="text-gray-700 mb-4">
                This status typically means:
              </p>
              
              <ul className="space-y-3 text-gray-700 mb-6">
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2">•</span>
                  <span>An immigration officer has opened or reviewed your file</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2">•</span>
                  <span>Background checks or security clearances may be progressing</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2">•</span>
                  <span>Your case is moving through the system (not sitting untouched)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2">•</span>
                  <span>The officer may be preparing a decision, requesting more info, or scheduling next steps</span>
                </li>
              </ul>

              <p className="text-gray-700">
                <strong>Important:</strong> "Actively reviewed" does NOT mean your case is approved. 
                It just means there's movement. Think of it like a package that's "in transit"—it's moving, but hasn't arrived yet.
              </p>
            </section>

            {/* Section 2 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Is This Good News?
              </h2>
              <p className="text-gray-700 mb-4">
                Mostly, yes—but with realistic expectations.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <h3 className="font-semibold text-green-900 mb-2 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    The Good
                  </h3>
                  <ul className="space-y-2 text-green-800">
                    <li className="flex items-start">
                      <span className="mr-2">✓</span>
                      <span>Your case is not stuck or forgotten</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">✓</span>
                      <span>USCIS is actively working on it</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">✓</span>
                      <span>You should see another update soon</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                  <h3 className="font-semibold text-amber-900 mb-2 flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    The Reality
                  </h3>
                  <ul className="space-y-2 text-amber-800">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Not a guarantee of approval</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>You might get an RFE or interview notice</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Could stay like this for weeks or months</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 3 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                What Can Happen Next?
              </h2>
              <p className="text-gray-700 mb-6">
                After "actively reviewed," your case could go in several directions:
              </p>

              <div className="space-y-4">
                {nextSteps.map((step, index) => (
                  <div 
                    key={index}
                    className="flex items-start p-5 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition"
                  >
                    <div className="text-indigo-600 mr-4 mt-1">
                      {step.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{step.title}</h3>
                      <p className="text-gray-600">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 4 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                How Long After "Actively Reviewed" Until the Next Update?
              </h2>
              <p className="text-gray-700 mb-4">
                The honest answer: <strong>it varies a lot</strong>.
              </p>
              
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="font-semibold mr-2 text-indigo-600">Days:</span>
                    <span>Some cases get approved or receive an RFE within a few days</span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-semibold mr-2 text-indigo-600">Weeks:</span>
                    <span>More common—cases can sit in "actively reviewed" for 2-6 weeks</span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-semibold mr-2 text-indigo-600">Months:</span>
                    <span>Yes, it happens. Complex cases or backlogged offices can take months</span>
                  </li>
                </ul>
              </div>

              <p className="text-gray-700">
                <strong>Factors that affect timing:</strong> Your form type (I-485, I-130, N-400, etc.), 
                which USCIS field office or service center is processing it, current backlogs, 
                and whether you need an interview or additional security checks.
              </p>
            </section>

            {/* Section 5 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                What Should You Do Now?
              </h2>
              <p className="text-gray-700 mb-6">
                The best strategy: <strong>stay prepared, but be patient</strong>.
              </p>

              <div className="bg-indigo-50 border-l-4 border-indigo-500 rounded-r-lg p-6">
                <h3 className="font-semibold text-indigo-900 mb-4">Action Items:</h3>
                <ul className="space-y-3">
                  {actionItems.map((item, index) => (
                    <li key={index} className="flex items-start text-indigo-900">
                      <CheckCircle className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 text-indigo-600" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-gray-700 mt-6">
                <strong>Don't panic if nothing changes for a while.</strong> Immigration is slow. 
                As long as you're within normal processing times, trust the process.
              </p>
            </section>

            {/* FAQ Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Common Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div 
                    key={index}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    <button
                      onClick={() => toggleFAQ(index)}
                      className="w-full px-6 py-4 text-left bg-white hover:bg-gray-50 transition flex items-center justify-between"
                    >
                      <h3 className="font-semibold text-gray-900 pr-4">
                        {faq.question}
                      </h3>
                      {openFAQ === index ? (
                        <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      )}
                    </button>
                    {openFAQ === index && (
                      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                        <p className="text-gray-700">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* CTA Section */}
            <section className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-8 text-center text-white">
              <h2 className="text-3xl font-bold mb-4">
                Never Miss a USCIS Update Again
              </h2>
              <p className="text-lg mb-6 text-indigo-100">
                Track your case automatically, get instant email alerts for every status change, and understand what each update means in plain English.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-4">
                <Link
                  to="/pricing"
                  className="inline-block bg-white text-indigo-600 font-semibold px-8 py-3 rounded-lg hover:bg-indigo-50 transition shadow-lg"
                >
                  Start Tracking Your Case
                </Link>
                <Link
                  to="/register"
                  className="inline-block bg-indigo-700 text-white font-semibold px-8 py-3 rounded-lg hover:bg-indigo-800 transition border-2 border-white"
                >
                  Try Free Check
                </Link>
              </div>
              <p className="text-sm text-indigo-100">
                ✓ Track up to 5 cases  ✓ Automatic status checks  ✓ Email alerts  ✓ Full case history
              </p>
            </section>
          </article>

          {/* Related Articles */}
          <aside className="mt-8 bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Related USCIS Guides</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/pricing" className="text-indigo-600 hover:text-indigo-700 hover:underline">
                → How USCIS Tracker Works
              </Link>
              <Link to="/pricing" className="text-indigo-600 hover:text-indigo-700 hover:underline">
                → Automatic Case Status Monitoring
              </Link>
              <Link to="/register" className="text-indigo-600 hover:text-indigo-700 hover:underline">
                → Check Your Case Status (Free)
              </Link>
              <Link to="/pricing" className="text-indigo-600 hover:text-indigo-700 hover:underline">
                → Track Multiple Cases (Pro)
              </Link>
            </div>
          </aside>
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 mt-12">
          <div className="max-w-4xl mx-auto px-4 py-8 text-center text-gray-600">
            <p>© 2025 USCIS Tracker - Automatic Case Status Monitoring with Email Alerts</p>
            <div className="mt-2 space-x-4">
              <Link to="/pricing" className="text-indigo-600 hover:text-indigo-700">Pricing</Link>
              <Link to="/login" className="text-indigo-600 hover:text-indigo-700">Login</Link>
              <Link to="/" className="text-indigo-600 hover:text-indigo-700">Home</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
